import { useEffect, useState } from "react";
import { api, apiError, dateTimeOf } from "@/lib/api";
import { GoogleSignIn } from "@/pages/auth/GoogleSignIn";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, GoogleLogo, Key, ClockCounterClockwise } from "@phosphor-icons/react";

const Card = ({ icon: Icon, tone, title, children, testid }) => (
  <div className="bg-white rounded-2xl border border-border p-6" data-testid={testid}>
    <div className="flex items-start gap-4">
      <span className={`h-12 w-12 rounded-2xl grid place-items-center shrink-0 ${tone}`}><Icon size={24} weight="duotone" /></span>
      <div className="flex-1 min-w-0"><p className="font-bold text-base">{title}</p>{children}</div>
    </div>
  </div>
);

/** Two-step verification, password, linked sign-ins and recent sign-in history for the current user. */
export const AccountSecurity = () => {
  const { user, reload } = useAuth();
  const [history, setHistory] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [pw, setPw] = useState("");
  const [change, setChange] = useState({ current: "", next: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/auth/login-history").then(({ data }) => setHistory(data)).catch(() => {}); }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/toggle", { enabled: confirm, password: pw });
      toast.success(confirm ? "Two-step verification is on" : "Two-step verification is off");
      setConfirm(null); setPw(""); await reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: change.current, new_password: change.next });
      toast.success(user.has_password ? "Password changed" : "Password set"); setChange({ current: "", next: "" }); await reload();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="account-security">
      <Card icon={ShieldCheck} tone="bg-emerald-50 text-[#10B981]" title="Two-step verification" testid="twofa-card">
        <p className="text-sm text-muted-foreground mt-1">
          {user.twofa_required
            ? "Required for your role. Each sign-in asks for a 6-digit code emailed to you."
            : "Adds a 6-digit code emailed to you each time you sign in. Recommended."}
        </p>
        <div className="mt-3 flex items-center gap-3">
          <Switch checked={user.twofa_required || user.twofa_enabled} disabled={user.twofa_required || busy}
                  onCheckedChange={(v) => setConfirm(v)} data-testid="twofa-switch" />
          <span className="text-sm font-bold" data-testid="twofa-status">{user.twofa_required ? "Always on (required)" : user.twofa_enabled ? "On" : "Off"}</span>
        </div>
      </Card>

      <Card icon={Key} tone="bg-blue-50 text-[#3B82F6]" title={user.has_password ? "Change password" : "Set a password"} testid="password-card">
        <form onSubmit={changePassword} className="mt-3 grid sm:grid-cols-2 gap-3">
          {user.has_password && <div><Label className="text-xs font-bold">Current password</Label><Input type="password" required value={change.current} onChange={(e) => setChange({ ...change, current: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="pw-current" /></div>}
          <div><Label className="text-xs font-bold">New password</Label><Input type="password" required value={change.next} onChange={(e) => setChange({ ...change, next: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="pw-next" /></div>
          <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={busy} className="rounded-full font-bold bg-[#3B82F6] hover:bg-[#2563EB]" data-testid="pw-save">Save password</Button>
            <span className="text-xs text-muted-foreground">10+ characters with an uppercase letter, a lowercase letter and a number.</span>
          </div>
        </form>
      </Card>

      <Card icon={GoogleLogo} tone="bg-red-50 text-[#EF4444]" title="Sign-in methods" testid="providers-card">
        <p className="text-sm text-muted-foreground mt-1">
          {user.has_password ? "Email & password" : ""}{user.has_password && user.google_linked ? " · " : ""}{user.google_linked ? "Google" : ""}
          {!user.has_password && !user.google_linked ? "None yet" : ""}
        </p>
        {!user.google_linked && (
          <div className="mt-3" data-testid="link-google-btn">
            <p className="text-sm font-semibold mb-2">Link Google to {user.email}</p>
            <GoogleSignIn mode="link" text="continue_with" testid="link-google-widget"
                          onSession={() => { toast.success("Google sign-in linked to this account"); reload(); }}
                          onError={(m) => toast.error(m)} />
          </div>
        )}
      </Card>

      <Card icon={ClockCounterClockwise} tone="bg-amber-50 text-[#F59E0B]" title="Recent sign-ins" testid="login-history-card">
        <ul className="mt-3 divide-y divide-border text-sm" data-testid="login-history">
          {history.length === 0 && <li className="py-2 text-muted-foreground">No sign-ins recorded yet.</li>}
          {history.slice(0, 10).map((h) => (
            <li key={h.id} className="py-2 flex flex-wrap justify-between gap-2">
              <span><span className={`font-bold ${h.success ? "text-emerald-700" : "text-[#EF4444]"}`}>{h.success ? "Signed in" : "Failed"}</span> · {h.method.replace("+", " + ")}{h.detail ? ` (${h.detail.replace(/_/g, " ")})` : ""}</span>
              <span className="text-muted-foreground">{dateTimeOf(h.at)}{h.ip ? ` · ${h.ip}` : ""}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Dialog open={confirm !== null} onOpenChange={() => { setConfirm(null); setPw(""); }}>
        <DialogContent className="rounded-3xl" data-testid="twofa-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{confirm ? "Turn on two-step verification" : "Turn off two-step verification"}</DialogTitle>
            <DialogDescription>{user.has_password ? "Confirm your password to continue." : "Confirm to continue."}</DialogDescription>
          </DialogHeader>
          {user.has_password && <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Current password" className="h-12 rounded-xl" data-testid="twofa-confirm-password" />}
          <Button onClick={toggle} disabled={busy || (user.has_password && !pw)} className="w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="twofa-confirm-btn">
            {busy ? "Saving…" : confirm ? "Turn on" : "Turn off"}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** Super-administrator policy: which roles must use 2FA, invitation expiry, idle timeout. */
export const SecurityPolicy = () => {
  const { user } = useAuth();
  const [sec, setSec] = useState(null);
  const [busy, setBusy] = useState(false);
  const readOnly = user.role !== "super_admin";

  useEffect(() => { api.get("/security/settings").then(({ data }) => setSec(data)).catch(() => {}); }, []);
  if (!sec) return null;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/security/settings", { twofa_required_roles: sec.twofa_required_roles, invitation_expiry_days: sec.invitation_expiry_days,
                                                             session_idle_minutes: sec.session_idle_minutes, open_registration: sec.open_registration });
      setSec({ ...sec, ...data }); toast.success("Security policy saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const toggleRole = (r) => setSec({ ...sec, twofa_required_roles: sec.twofa_required_roles.includes(r) ? sec.twofa_required_roles.filter((x) => x !== r) : [...sec.twofa_required_roles, r] });

  return (
    <div className="mt-10 max-w-xl" data-testid="security-policy">
      <h2 className="text-2xl font-bold tracking-tight">Security policy</h2>
      <p className="mt-2 text-muted-foreground text-sm">{readOnly ? "Only a super administrator can change these." : "Applies to everyone who signs in."}</p>
      <div className="mt-4 bg-white rounded-2xl border border-border p-6 space-y-5">
        <div>
          <Label className="text-sm font-bold">Roles that must use two-step verification</Label>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="twofa-roles">
            {sec.roles.map((r) => {
              const locked = sec.twofa_locked_roles.includes(r.key);
              return (
                <label key={r.key} className={`text-xs font-semibold flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${locked ? "border-[#3B82F6] bg-blue-50" : "border-border"}`}>
                  <input type="checkbox" checked={sec.twofa_required_roles.includes(r.key)} disabled={locked || readOnly} onChange={() => toggleRole(r.key)} data-testid={`twofa-role-${r.key}`} /> {r.label}{locked ? " (always)" : ""}
                </label>
              );
            })}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-sm font-bold">Invitation links expire after (days)</Label><Input type="number" min={1} max={60} disabled={readOnly} value={sec.invitation_expiry_days} onChange={(e) => setSec({ ...sec, invitation_expiry_days: Number(e.target.value) })} className="mt-2 h-11 rounded-xl" data-testid="policy-invite-days" /></div>
          <div><Label className="text-sm font-bold">Sign out after inactivity (minutes)</Label><Input type="number" min={5} max={480} disabled={readOnly} value={sec.session_idle_minutes} onChange={(e) => setSec({ ...sec, session_idle_minutes: Number(e.target.value) })} className="mt-2 h-11 rounded-xl" data-testid="policy-idle-minutes" /></div>
        </div>
        <p className="text-xs text-muted-foreground">Verification codes expire after {sec.otp_expiry_minutes} minutes, may be resent after {sec.otp_resend_seconds}s (max {sec.otp_max_resends} resends), and lock after {sec.otp_max_attempts} wrong tries. Public self-registration is {sec.open_registration ? "ON" : "off"} — accounts are invitation-only.</p>
        {!readOnly && <Button onClick={save} disabled={busy} className="rounded-full font-bold bg-[#3B82F6] hover:bg-[#2563EB]" data-testid="policy-save">Save policy</Button>}
      </div>
    </div>
  );
};
